/**
 * Generates smart nudges/notifications based on user's practice data.
 * Returns sorted array of nudge objects by priority.
 */

const SESSION_TYPES = ['Elevator Pitch', 'Casual Encounter'];

export const generateNudges = (assessments = [], streakData = {}, goals = null) => {
    const nudges = [];
    const now = new Date();

    // 1. Streak at risk (high priority)
    if (streakData.currentStreak > 0) {
        const dayOfWeek = now.getUTCDay(); // 0=Sun, UTC-based to match streak calculation
        const mondayOffset = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
        const weekStart = new Date(now);
        weekStart.setUTCDate(now.getUTCDate() - mondayOffset);
        weekStart.setUTCHours(0, 0, 0, 0);

        const thisWeekSubmissions = assessments.filter(a =>
            a.completedDateTime && new Date(a.completedDateTime) >= weekStart
        );

        if (thisWeekSubmissions.length === 0) {
            const daysLeft = 7 - mondayOffset;
            nudges.push({
                id: 'streak-at-risk',
                type: 'warning',
                priority: 1,
                icon: '🔥',
                title: 'Streak at Risk!',
                message: `Your ${streakData.currentStreak}-week streak will break if you don't practice by Sunday. ${daysLeft} day${daysLeft !== 1 ? 's' : ''} remaining.`,
                actionLabel: 'Practice Now',
                actionType: 'startRecording',
                sessionType: 'Elevator Pitch'
            });
        }
    }

    // 2. Grade decline (high priority)
    const graded = assessments
        .filter(a => a.grade && a.completedDateTime)
        .sort((a, b) => new Date(b.completedDateTime) - new Date(a.completedDateTime));

    if (graded.length >= 10) {
        const scoreMap = { green: 3, yellow: 2, red: 1 };
        const recent5 = graded.slice(0, 5).reduce((s, a) => s + (scoreMap[a.grade] || 0), 0) / 5;
        const prev5 = graded.slice(5, 10).reduce((s, a) => s + (scoreMap[a.grade] || 0), 0) / 5;
        if (recent5 < prev5 - 0.3) {
            nudges.push({
                id: 'grade-decline',
                type: 'warning',
                priority: 2,
                icon: '📉',
                title: 'Scores Trending Down',
                message: 'Your recent practice scores are lower than your previous sessions. Focus on areas for improvement.',
            });
        }
    }

    // 3. Inactivity per session type (medium priority)
    SESSION_TYPES.forEach(sessionType => {
        const typeAssessments = assessments.filter(a => {
            const t = a.sessionType === 'Sales Presentation / Demo' ? 'Sales Presentation' : a.sessionType;
            return t === sessionType && a.completedDateTime;
        });

        if (typeAssessments.length > 0) {
            const latest = typeAssessments.sort((a, b) =>
                new Date(b.completedDateTime) - new Date(a.completedDateTime)
            )[0];
            const _last = new Date(latest.completedDateTime);
            const _todayMid = new Date(now.getFullYear(), now.getMonth(), now.getDate());
            const _lastMid = new Date(_last.getFullYear(), _last.getMonth(), _last.getDate());
            const daysSince = Math.round((_todayMid - _lastMid) / (1000 * 60 * 60 * 24));
            const threshold = sessionType === 'Sales Presentation' ? 30 : 7;

            if (daysSince > threshold) {
                nudges.push({
                    id: `inactivity-${sessionType}`,
                    type: 'info',
                    priority: 3,
                    icon: '⏰',
                    title: `${sessionType} Overdue`,
                    message: `It's been ${daysSince} days since your last ${sessionType}. Time to practice!`,
                    actionLabel: 'Start Now',
                    actionType: 'startRecording',
                    sessionType
                });
            }
        }
    });

    // 4. Goal behind (medium priority)
    if (goals && goals.weeklyTarget > 0) {
        const dayOfWeek = now.getUTCDay();
        const mondayOffset = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
        const weekStart = new Date(now);
        weekStart.setUTCDate(now.getUTCDate() - mondayOffset);
        weekStart.setUTCHours(0, 0, 0, 0);

        const weeklyDone = assessments.filter(a =>
            a.completedDateTime && new Date(a.completedDateTime) >= weekStart
        ).length;

        // Past mid-week (Wednesday+) and behind target pace
        if (mondayOffset >= 2 && weeklyDone < Math.floor(goals.weeklyTarget * mondayOffset / 7)) {
            nudges.push({
                id: 'goal-behind',
                type: 'info',
                priority: 4,
                icon: '🎯',
                title: 'Behind Weekly Goal',
                message: `You've completed ${weeklyDone} of ${goals.weeklyTarget} practices this week. Pick up the pace!`,
                actionLabel: 'Practice Now',
                actionType: 'startRecording',
                sessionType: 'Elevator Pitch'
            });
        }
    }

    // 5. Never practiced session type (low priority)
    SESSION_TYPES.forEach(sessionType => {
        const hasType = assessments.some(a => {
            const t = a.sessionType === 'Sales Presentation / Demo' ? 'Sales Presentation' : a.sessionType;
            return t === sessionType;
        });
        if (!hasType && assessments.length > 0) {
            nudges.push({
                id: `never-practiced-${sessionType}`,
                type: 'info',
                priority: 5,
                icon: '💡',
                title: `Try ${sessionType}`,
                message: `You haven't practiced a ${sessionType} yet. Give it a try!`,
                actionLabel: 'Start Now',
                actionType: 'startRecording',
                sessionType
            });
        }
    });

    return nudges.sort((a, b) => a.priority - b.priority).slice(0, 3);
};
